import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { billingApi, type BillingReceipt, type PendingPurchase, type PlanListItem, type CurrentPlan } from '../../services/billing';
import { TableWrapper, TableHead } from '../../components/TableWrapper';

const PLAN_ORDER: Record<string, number> = { A: 1, B: 2, C: 3 };

function formatSek(value: number): string {
  return `${value.toLocaleString('sv-SE')} kr`;
}

function formatDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('sv-SE');
}

export function SettingsPlanPage() {
  const { permissions } = useAuth();
  const { error, success, info } = useToast();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<PlanListItem[]>([]);
  const [current, setCurrent] = useState<CurrentPlan | null>(null);
  const [receipts, setReceipts] = useState<BillingReceipt[]>([]);
  const [pendingPurchase, setPendingPurchase] = useState<PendingPurchase | null>(null);
  const [quotes, setQuotes] = useState<Record<string, number>>({});
  const [busyPlanCode, setBusyPlanCode] = useState<string | null>(null);

  const isAdmin = permissions.settings === 'ADMIN';

  const load = useCallback(async () => {
    try {
      const [currentRes, planRes, receiptRes] = await Promise.all([
        billingApi.getCurrentPlan(),
        billingApi.getPlanList(),
        billingApi.listReceipts(),
      ]);
      const pendingRes = await billingApi.getPendingPurchase();
      setCurrent(currentRes);
      setPlans(planRes.plans);
      setReceipts(receiptRes.receipts);
      setPendingPurchase(pendingRes.pendingPurchase);
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte ladda planinformation');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!current || plans.length === 0) return;
    let cancelled = false;
    (async () => {
      const nextQuotes: Record<string, number> = {};
      for (const p of plans) {
        if ((PLAN_ORDER[p.planCode] ?? 0) <= (PLAN_ORDER[current.planCode] ?? 0)) continue;
        try {
          const q = await billingApi.quoteUpgrade(p.planCode);
          nextQuotes[p.planCode] = q.amountToPaySek;
        } catch {
          nextQuotes[p.planCode] = -1;
        }
      }
      if (!cancelled) setQuotes(nextQuotes);
    })();
    return () => {
      cancelled = true;
    };
  }, [current, plans]);

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => (PLAN_ORDER[a.planCode] ?? 0) - (PLAN_ORDER[b.planCode] ?? 0)),
    [plans]
  );

  const handleUpgrade = async (targetPlanCode: 'A' | 'B' | 'C') => {
    setBusyPlanCode(targetPlanCode);
    try {
      const res = await billingApi.initPurchase(targetPlanCode);
      success(`Köp initierat (${res.paymentInstructions.providerRef})`);
      info(res.paymentInstructions.message);
      await load();
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte initiera köp');
    } finally {
      setBusyPlanCode(null);
    }
  };

  const handleDeletePending = async () => {
    if (!pendingPurchase) return;
    setBusyPlanCode('PENDING');
    try {
      await billingApi.cancelPendingPurchase(pendingPurchase.id);
      success('Väntande uppgradering raderad');
      await load();
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte radera väntande uppgradering');
    } finally {
      setBusyPlanCode(null);
    }
  };

  const handlePrintReceipt = async (id: number) => {
    try {
      const receipt = await billingApi.getReceipt(id);
      const logoUrl = `${window.location.origin}/logga.png`;
      const html = `
<!doctype html>
<html lang="sv">
<head>
  <meta charset="utf-8">
  <title>Kvitto ${receipt.receiptNumber}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 24px; color: #222; }
    .header { display:flex; align-items:center; justify-content:space-between; margin-bottom: 20px; }
    .header img { height: 40px; }
    table { border-collapse: collapse; width: 100%; max-width: 620px; }
    td { padding: 6px 0; }
    .right { text-align: right; }
    .footer { margin-top: 40px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 12px; color: #444; line-height: 1.5; }
  </style>
</head>
<body>
  <div class="header">
    <img src="${logoUrl}" alt="ApisAI" />
    <div>
      <div><strong>Kvitto ${receipt.receiptNumber}</strong></div>
      <div>Plan ${receipt.planCode} - ${receipt.status}</div>
    </div>
  </div>

  <table>
    <tr><td>Företag</td><td class="right">${receipt.businessName}</td></tr>
    <tr><td>Köpt</td><td class="right">${formatDateTime(receipt.purchasedAt)}</td></tr>
    <tr><td>Belopp inkl moms</td><td class="right">${formatSek(receipt.grossAmountSek)}</td></tr>
    <tr><td>Moms</td><td class="right">${formatSek(receipt.vatAmountSek)}</td></tr>
    <tr><td><strong>Belopp ex moms</strong></td><td class="right"><strong>${formatSek(receipt.netAmountSek)}</strong></td></tr>
  </table>

  <div class="footer">
    <div><strong>ApisAI</strong></div>
    <div>Strömbergs Bisysslor</div>
    <div>Sågverksvägen 10</div>
    <div>577 75 Mörlunda</div>
    <div>nicklas@strombergsbisysslor.se</div>
    <div>apisai.se</div>
    <div>Org nr 740911-2914</div>
    <div>Telefon 070-205 49 78</div>
  </div>
</body>
</html>`;
      const w = window.open('', '_blank', 'width=900,height=700');
      if (!w) {
        error('Popup blockerad - tillåt popup för att skriva ut kvitto');
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte hämta kvitto');
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Laddar...</p>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Plan och fakturering</h2>
      {current && (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <div>Aktuell plan: <strong>{current.planName}</strong> ({current.planCode})</div>
          <div>Giltig till: {formatDateTime(current.endAt)}</div>
          <div>Dagar kvar: {current.daysLeft}</div>
        </div>
      )}
      {pendingPurchase && (
        <div className="rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm flex items-center justify-between">
          <div>
            Väntande uppgradering: <strong>{pendingPurchase.currentPlanCode} -&gt; {pendingPurchase.targetPlanCode}</strong> ({formatSek(pendingPurchase.amountToPaySek)})
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={handleDeletePending}
              disabled={busyPlanCode === 'PENDING'}
              className="px-2 py-1 rounded bg-danger text-white hover:bg-danger-hover disabled:opacity-50"
            >
              {busyPlanCode === 'PENDING' ? 'Raderar...' : 'Radera pending'}
            </button>
          )}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        {sortedPlans.map((p) => {
          const isCurrent = current?.planCode === p.planCode;
          const canUpgrade = !!current && (PLAN_ORDER[p.planCode] ?? 0) > (PLAN_ORDER[current.planCode] ?? 0);
          const quote = quotes[p.planCode];
          return (
            <article key={p.planCode} className={`rounded border p-3 ${isCurrent ? 'border-primary' : 'border-gray-200'}`}>
              <h3 className="font-semibold">{p.planName}</h3>
              <p className="text-sm text-gray-600">{p.description}</p>
              <p className="mt-2 text-sm">Pris/år inkl moms: <strong>{formatSek(p.pricePerYearSek)}</strong></p>
              <ul className="mt-2 list-disc pl-4 text-sm text-gray-700">
                {p.includes.map((inc) => <li key={inc}>{inc}</li>)}
              </ul>
              {isCurrent && <p className="mt-3 text-sm text-primary font-medium">Aktiv plan</p>}
              {!isCurrent && canUpgrade && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-gray-600">Att betala nu: {quote >= 0 ? formatSek(quote) : 'N/A'}</p>
                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={() => handleUpgrade(p.planCode)}
                      disabled={busyPlanCode === p.planCode || !!pendingPurchase}
                      className="px-2 py-1 text-sm bg-success text-white rounded hover:bg-success-hover disabled:opacity-50"
                    >
                      {busyPlanCode === p.planCode ? 'Initierar...' : 'Uppgradera'}
                    </button>
                  ) : (
                    <p className="text-xs text-gray-600">Endast workspace ADMIN kan uppgradera.</p>
                  )}
                  {pendingPurchase && (
                    <p className="text-xs text-gray-600">En pending uppgradering finns redan.</p>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <section className="space-y-2">
        <h3 className="font-semibold">Kvitton</h3>
        <TableWrapper>
          <table className="w-full text-sm border-collapse">
            <TableHead>
              <tr>
                <th className="text-left px-2 py-1.5 font-medium">Kvittonummer</th>
                <th className="text-left px-2 py-1.5 font-medium">Plan</th>
                <th className="text-left px-2 py-1.5 font-medium">Belopp</th>
                <th className="text-left px-2 py-1.5 font-medium">Status</th>
                <th className="text-left px-2 py-1.5 font-medium">Datum</th>
                <th className="text-left px-2 py-1.5 font-medium">Åtgärd</th>
              </tr>
            </TableHead>
            <tbody>
              {receipts.length === 0 ? (
                <tr>
                  <td className="px-2 py-2 text-gray-500" colSpan={6}>Inga kvitton ännu.</td>
                </tr>
              ) : receipts.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-2 py-1.5">{r.receiptNumber}</td>
                  <td className="px-2 py-1.5">{r.planCode}</td>
                  <td className="px-2 py-1.5">{formatSek(r.grossAmountSek)}</td>
                  <td className="px-2 py-1.5">{r.status}</td>
                  <td className="px-2 py-1.5">{formatDateTime(r.purchasedAt)}</td>
                  <td className="px-2 py-1.5">
                    {r.status === 'PAID' ? (
                      <button type="button" className="text-primary hover:underline" onClick={() => handlePrintReceipt(r.id)}>
                        Skriv ut
                      </button>
                    ) : (
                      <span className="text-gray-400">Ej betald</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      </section>
    </div>
  );
}
